import React, { useCallback, useState, useEffect, useRef } from 'react';
import { DatePicker, Form, Input, Button, Select } from 'antd';
import { getRangeDate, setRangeDate, clearRangeDate } from 'utils/dateRangeStorage';
import { FormProps } from 'rc-field-form';
import 'styles/consolidate-revenue.scss';

import ExportServicesReport from 'components/common/export-services-report';
import ServiceTab from 'components/modal/consolidate-components/service-report';

import { generateExportFileName, getEarliestValidDate, toUtcTime } from 'utils';

import set from 'lodash/set';
import dayjs, { Dayjs } from 'dayjs';

import { useTranslation } from 'react-i18next';
import { getServiceProductIdOptions, SERVICES_PRODUCT_ID } from 'components/common/constant';
import useCommon from 'stores/useCommon';
import IBButton from 'helper/permission.action';
import { PermissionActionTypes } from 'helper/type/action.type';
import useModal from 'stores/useModal';
import { useSelectMultiBranches } from 'hooks/useSelectMultiBranches';
import SelectMultiBranchesModal from 'components/common/select-multi-branches-modal';

const INITIAL_RANGE_DATE = 14;
const initStartDate = dayjs().startOf('day');

function ServiceReport() {
  const { isOpenSourceBranchesModal, setIsOpenSourceBranchesModal } = useModal();
  const { currentBranch } = useCommon();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState<any[]>();
  const isFirstRender = useRef(true);

  const {
    selectedSourceBranches,
    setSelectedSourceBranches,
    setAppliedSourceBranches,
    hasSourceBranchesPermission,
    hasAppliedSourceBranches,
    setHasAppliedSourceBranches,
    selectedBranchesData,
    getBranchIdsString
  } = useSelectMultiBranches(PermissionActionTypes.SOURCE_REVENUE_BRANCHES);

  const [filterOptions, setFilterOptions] = useState<any>(() => {
    const savedRange = getRangeDate();
    if (savedRange) {
      const startDate = dayjs(savedRange.startDate);
      return {
        start_date: toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'),
        period: savedRange.period,
        product_id: SERVICES_PRODUCT_ID.transportation
      };
    }
    return {
      start_date: toUtcTime(initStartDate.add(1, 'day'), 'YYYY-MM-DD'),
      period: INITIAL_RANGE_DATE,
      product_id: SERVICES_PRODUCT_ID.transportation
    };
  });

  const onFinish: FormProps['onFinish'] = async (objValue: any) => {
    const truthyValues: any = Object.keys(objValue)
      .filter(key => Boolean(objValue[key]))
      .reduce((cur, next) => {
        return {
          ...cur,
          [next]: objValue[next]
        };
      }, {});

    const { rangeDate, period, product_id, ...restFilterOptions } = truthyValues;
    if (rangeDate) {
      const startDate: Dayjs = rangeDate[0];
      const endDate: Dayjs = rangeDate[1];

      if (startDate && endDate) {
        const dayCount = endDate.diff(startDate, 'day');
        set(restFilterOptions, 'period', dayCount + 1);
        set(restFilterOptions, 'start_date', toUtcTime(startDate.add(1, 'day'), 'YYYY-MM-DD'));
      }
    }

    set(restFilterOptions, 'period', period || INITIAL_RANGE_DATE);
    set(restFilterOptions, 'product_id', product_id || SERVICES_PRODUCT_ID.transportation);

    setFilterOptions(restFilterOptions);

    if (hasSourceBranchesPermission) {
      setHasAppliedSourceBranches(false);
    }
  };

  const handleRangeDateChange = (dates: any) => {
    if (dates && dates.length === 2) {
      const [start, end] = dates;
      const dayCount = end.diff(start, 'day');
      form.setFieldsValue({ period: dayCount + 1 });
      setRangeDate(start, end, dayCount + 1);
    }
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const period = parseInt(e.target.value);
    if (!isNaN(period) && period > 0) {
      const rangeDate = form.getFieldValue('rangeDate');
      if (rangeDate && rangeDate[0]) {
        const startDate = dayjs(rangeDate[0]);
        const endDate = startDate.add(period - 1, 'day');
        form.setFieldsValue({ rangeDate: [startDate, endDate] });
        setRangeDate(startDate, endDate, period);
      }
    }
  };

  const fromDate = getEarliestValidDate(filterOptions.start_date);

  const toDate = filterOptions.start_date
    ? dayjs(filterOptions.start_date)
        .add(filterOptions.period, 'day')
        .subtract(1, 'day')
        .format('DD-MM-YYYY')
    : undefined;

  const handleSetDataSource = useCallback((data: any[]) => {
    if (!data.length) return;
    setDataSource(data);
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      const savedRange = getRangeDate();
      if (savedRange) {
        const startDate = dayjs(savedRange.startDate);
        const endDate = dayjs(savedRange.endDate);
        form.setFieldsValue({
          rangeDate: [startDate, endDate],
          period: savedRange.period,
          product_id: SERVICES_PRODUCT_ID.transportation
        });
      } else {
        const endDate = initStartDate.add(INITIAL_RANGE_DATE - 1, 'day');
        form.setFieldsValue({
          rangeDate: [initStartDate, endDate],
          period: INITIAL_RANGE_DATE,
          product_id: SERVICES_PRODUCT_ID.transportation
        });
      }
    }
  }, [form]);

  useEffect(() => {
    window.addEventListener('beforeunload', () => {
      clearRangeDate();
    });

    return () => {
      window.removeEventListener('beforeunload', () => {
        clearRangeDate();
      });
    };
  }, []);

  return (
    <div className="pms-consolidate-revenue">
      <div className="pms-consolidate-revenue__timetable">
        <Form form={form} onFinish={onFinish} autoComplete="off">
          <div className="pms-consolidate-revenue__filter">
            <div className="pms-consolidate-revenue__filter__left">
              <Form.Item name="rangeDate">
                <DatePicker.RangePicker
                  className="w-full"
                  placeholder={[t('common.time.fromDate'), t('common.time.toDate')]}
                  format="DD-MM-YYYY"
                  onChange={handleRangeDateChange}
                  allowClear={false}
                />
              </Form.Item>
              <Form.Item name="period">
                <Input placeholder={t('common.time.totalDays')} onChange={handlePeriodChange} />
              </Form.Item>
              <Form.Item name="product_id">
                <Select
                  style={{ width: 220 }}
                  options={getServiceProductIdOptions(t)}
                  placeholder={t('managePage.consolidateRevenueReportPage.common.titleServices')}
                />
              </Form.Item>
              <Form.Item>
                <Button htmlType="submit" className="ant-btn-secondary btn-submit">
                  {t('common.actions.submitButton')}
                </Button>
              </Form.Item>
              <Form.Item>
                <IBButton
                  key="ok"
                  permission={PermissionActionTypes.SOURCE_REVENUE_BRANCHES}
                  className="ant-btn-secondary"
                  onClick={() => setIsOpenSourceBranchesModal(true)}
                >
                  {t('managePage.consolidateRevenueReportPage.actions.sourceRevenueBranches')}
                </IBButton>
              </Form.Item>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <ExportServicesReport
                fileName={generateExportFileName('services-report')}
                fromDate={fromDate}
                toDate={toDate}
                dataToExport={dataSource || []}
                t={t}
                selectedBranchesData={selectedBranchesData}
              />
            </div>
          </div>
        </Form>
        <div className="pms-consolidate-revenue__table">
          <ServiceTab
            filterOptions={filterOptions}
            isEnable={
              hasAppliedSourceBranches || !hasSourceBranchesPermission || !!currentBranch?.id
            }
            setDataSource={handleSetDataSource}
            branch_ids={getBranchIdsString()}
          />
        </div>
      </div>
      <SelectMultiBranchesModal
        open={isOpenSourceBranchesModal}
        onCancel={() => setIsOpenSourceBranchesModal(false)}
        onOk={() => {
          setAppliedSourceBranches(selectedSourceBranches);
          setHasAppliedSourceBranches(true);
          setIsOpenSourceBranchesModal(false);
        }}
        selectedSourceBranches={selectedSourceBranches}
        setSelectedSourceBranches={setSelectedSourceBranches}
      />
    </div>
  );
}

export default ServiceReport;
